import React, { useEffect, useState, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Card, Flex, Heading, Input, Text, useToast } from "@chakra-ui/react";
import AsyncSelect from 'react-select/async';
import FlowireAPI from "../../../flowireapi/FlowireAPI";
import { RappidContext } from '../Diagram';
import { updateLinksWithAgMatrix } from '../services/adgacencyMatrix/adgacencyMatrixService';

const FeedTagWizard = ({ element, type, closeFeedTagPopup, on_Screen_Menu_Focus }) => {
    if (!element.attributes['Fd_components']) {
        element.attributes['Fd_components'] = {};
    }
    const rappid = useContext(RappidContext);
    const [components, setComponents] = useState([]);
    const [compoundInput, setCompoundInput] = useState(null);
    const [addingCompound, setAddingCompound] = useState(false);
    const [compoundMWeight, setCompoundMWeight] = useState("");
    const [compoundName, setCompoundName] = useState("");
    const [updateCustomCompoundsState, setUpdateCustomCompounds] = useState(true);
    const toast = useToast();
    const { isAuthenticated, getIdTokenClaims } = useAuth0();
    const [customCompounds, setCustomCompounds] = useState({});
    const [componentsToRemove, setComponentsToRemove] = useState([]);
    const [isAddCompoundDisabled, setIsAddCompoundDisabled] = useState(true);
    const [isDeleteCompoundDisabled, setIsDeleteCompoundDisabled] = useState(true);
    const [compoundToDelete, setCompoundToDelete] = useState(null);
    const [isDeleteCompoundListDisabled, setIsDeleteCompoundListDisabled] = useState(true);
    const [compoundMFormula, setCompoundMFormula] = useState("");

    useEffect(() => {
        if (element.attributes['Fd_components']) {
            let comps = [];
            for (const [key, value] of Object.entries(element.attributes['Fd_components'])) {
                comps.push({
                    compName: key,
                    mW: value.M
                });
            }
            setComponents(comps);
        } else {
            element.attributes['Fd_components'] = {};
        }
    }, [element]);

    const handleAddComponent = async (compoundName) => {
        if (!compoundName) {
            toast({
                title: "Please enter a compound name.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            let details = await fetchCompoundDetails(compoundName);
            if (!details) {
                toast({
                    title: `Compound not found for ${compoundName}.`,
                    description: 'Add as custom compound or try another compound.',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const newComponent = {
                compName: details.iupacName || compoundName,
                mW: details.molecularWeight
            };

            const isExisting = components.some(comp => comp.compName === newComponent.compName);
            if (isExisting) {
                toast({
                    title: "This compound has already been added.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            setComponents(prevComponents => [...prevComponents, newComponent]);
            toast({
                title: "Successfully added the compound.",
                description: newComponent.compName,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            setCompoundInput(null);
            setCompoundName("");
            setCompoundMWeight("");
            setCompoundMFormula("");

        } catch (error) {
            toast({
                title: "Failed to add compound.",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchCompoundDetails = async (compoundName) => {
        let isCustomCompound = checkIsCustomCompound(compoundName);
        if (isCustomCompound) {
            const [molecularFormula, molecularWeight] = customCompounds[compoundName];
            return { 
                iupacName: compoundName,
                molecularWeight,
                molecularFormula
            };
        } else {
            try {
                const cleanCompoundName = compoundName.replace(/_/g, ' ');
                const response = await FlowireAPI.get(`https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(cleanCompoundName)}/json`);
                if (response && response.PC_Compounds && response.PC_Compounds.length > 0) {
                    const compound = response.PC_Compounds[0];
                    return {
                        iupacName: (() => { const n = compound.props.find(prop => prop.urn.label === 'IUPAC Name' && prop.urn.name === 'Allowed')?.value.sval; return (n && !n.includes(';')) ? n : cleanCompoundName; })(),
                        molecularWeight: compound.props.find(prop => prop.urn.label === 'Molecular Weight')?.value.sval || '',
                        molecularFormula: compound.props.find(prop => prop.urn.label === 'Molecular Formula')?.value.sval || ''
                    };
                }
            } catch (error) {
                console.error(`Error fetching details for ${compoundName}:`, error);
            }
            return null;
        }
    };

    const checkIsCustomCompound = (compoundName) => {
        return customCompounds.hasOwnProperty(compoundName);
    };

    const removeComponent = (compToRemove) => {
        setComponents(prevComponents => 
            prevComponents.filter(comp => comp.compName !== compToRemove)
        );
        setComponentsToRemove(prevComponentsToRemove => 
            [...prevComponentsToRemove, { compName: compToRemove }]
        );
    };

    const handleFinish = () => {
        console.log("Handle Finish Called");

        componentsToRemove.forEach((item) => {
            delete element.attributes['Fd_components'][item.compName.replace(/\s/g, '_')];
        });

        components.forEach((item) => {
            const componentName = item.compName.replace(/\s/g, '_');
            element.attributes['Fd_components'][componentName] = {
                M: item.mW
            };
        });

        console.log("Updated element:", element);

        if (rappid) {
            const graph = rappid.getGraph();
            const updatedMatrix = updateLinksWithAgMatrix(graph, rappid);
            console.log("Updated Agency Matrix:", updatedMatrix);
        }

        on_Screen_Menu_Focus(false);
        closeFeedTagPopup();
    };

    const handleChange = async (newValue) => {
        setCompoundInput(newValue);
        if (newValue) {
            const compoundName = newValue.label;
            setCompoundName(compoundName);
            let details = await fetchCompoundDetails(compoundName);
            if (details) {
                setCompoundMWeight(details.molecularWeight);
                setCompoundMFormula(details.molecularFormula);
            }
        } else {
            setCompoundName("");
            setCompoundMWeight("");
            setCompoundMFormula("");
        }
    };

    const handleAddCustomCompoundClick = () => {
        setAddingCompound(true);
        setCompoundName("");
        setCompoundMWeight("");
        setCompoundMFormula("");
        setCompoundToDelete(null);
        setIsAddCompoundDisabled(true);
        setIsDeleteCompoundDisabled(true);
    };

    const updateCustomCompounds = async () => {
        return await FlowireAPI.authGet('/api/getcompounds', getIdTokenClaims, isAuthenticated, () => {
            return {};
        }).then((data) => {
            if (data) {
                return data.compounds;
            } else {
                return {};
            }
        });
    };
    
    const handleAddCompound = async () => {
        if (compoundName === "" || compoundMWeight === "" || compoundMFormula === "") {
            toast({
                title: "Compound name, molecular weight, or molecular formula was incorrectly provided.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Ensure molecular weight is greater than 0.
        if (parseFloat(compoundMWeight) <= 0) {
            toast({
            title: "Molecular weight must be greater than 0.",
            status: "error",
            duration: 3000,
            isClosable: true,
            });
            return;
        }

        const isDuplicate = customCompounds[compoundName] || 
            Object.values(customCompounds).some(([formula]) => formula === compoundMFormula);

        if (isDuplicate) {
            toast({
                title: "Duplicate compound.",
                description: "A compound with this name or formula already exists.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        await FlowireAPI.authPost('/api/addcompound', {
            "compound_name": compoundName,
            "compound_mweight": compoundMWeight,
            "compound_mformula": compoundMFormula
        }, getIdTokenClaims, isAuthenticated).then((data) => {
            if (data) {
                toast({
                    title: "Successfully added compound " + compoundName,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setCompoundName("");
                setCompoundMWeight("");
                setCompoundMFormula("");
                setAddingCompound(false);
                setUpdateCustomCompounds(true);
            } else {
                toast({
                    title: "Failed to add compound " + compoundName,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        });
    };

    const handleCancel = () => {
        setCompoundName("");
        setCompoundMWeight("");
        setCompoundMFormula("");
        setCompoundInput(null);
        setAddingCompound(false);
    };

    const handleClose = () => {
        on_Screen_Menu_Focus(false);
        closeFeedTagPopup();
    };

    const handleDeleteCompound = () => {
        if (!compoundToDelete) {
            toast({
                title: "No compound selected for deletion.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        FlowireAPI.authPost('/api/deletecompound', {
            "compound_name": compoundToDelete.label
        }, getIdTokenClaims, isAuthenticated).then((response) => {
            if (response) {
                toast({
                    title: "Successfully deleted compound " + compoundToDelete.label,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setCompoundToDelete(null);
                setUpdateCustomCompounds(true);
            } else {
                toast({
                    title: "Error deleting compound " + compoundToDelete.label,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        });
    };

    const fetchCompoundDropdown = async (inputValue) => {
        let customComps = customCompounds;
        if (updateCustomCompoundsState) {
            customComps = await updateCustomCompounds();
            setCustomCompounds(customComps);
            setUpdateCustomCompounds(false);
        }
        let data = {};
        await FlowireAPI.get('https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/' + encodeURIComponent(inputValue) + '/json', () => {}).then((response) => {
            if (response && response.PC_Compounds) {
                data[inputValue] = "";
            }
        });

        Object.assign(data, customComps);
        const options = Object.keys(data).map(compoundName => ({
            value: compoundName.toLowerCase(),
            label: compoundName
        }));
        let filteredData;
        if (inputValue !== "") {
            filteredData = options.filter((item) =>
                item.value.toLowerCase().includes(inputValue.toLowerCase())
            );
        } else {
            filteredData = options;
        }
        return filteredData;        
    };

    const promiseOptions = inputValue => new Promise(resolve => {
        fetchCompoundDropdown(inputValue).then(data => {
            resolve(data);
        }).catch(error => {
            console.error('Error fetching data:', error);
            resolve([]);
        });
    });

    const handleClickAddComponent = () => {
        if (compoundInput?.label) {
            handleAddComponent(compoundInput.label);
        } else {
            toast({
                title: "Please enter a compound name.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const getCompoundsList = inputValue => new Promise(resolve => {
        const getOps = async (inputValue) => {
            let data = await updateCustomCompounds();
            const options = Object.keys(data).map(compoundName => ({
                value: compoundName.toLowerCase(),
                label: compoundName
            }));
            let filteredData;
            if (inputValue !== "") {
                filteredData = options.filter((item) =>
                    item.value.toLowerCase().includes(inputValue.toLowerCase())
                );
            } else {
                filteredData = options;
            }
            return filteredData;
        };

        getOps(inputValue).then(data => {
            resolve(data);
        }).catch(error => {
            console.error('Error fetching data:', error);
            resolve([]);
        });
    });

    useEffect(() => {
        setIsAddCompoundDisabled(compoundName === "" || compoundMWeight === "" || compoundMFormula === "");
        setIsDeleteCompoundListDisabled(compoundName !== "" || compoundMWeight !== "" || compoundMFormula !== "");
    }, [compoundName, compoundMWeight, compoundMFormula]);

    return (
        <Card id="FeedTagWizard"
        borderRadius="10px" boxShadow="lg" bgColor="white" minWidth="340px"
        minHeight="200px" position={'absolute'} top="50%" transform="translateY(-50%)">
            <>
            {!addingCompound && (
                <>
                    {type == "Feed" ? (
                        <Flex
                        borderBottom='1px solid #eee'
                        justifyContent="space-between" alignItems="center"
                        pt={0} pb={2} pl={2} pr={2}
                        >
                        
                        <Heading size="sm" color="black">Add Components</Heading>
                        </Flex>
                    ) : (
                        <Text className="stream-property-label" width="100%" fontSize={15} float="left" color="#6767a8">Add Reaction Products</Text>
                    )}
                    <Box maxHeight="300px" overflowY="scroll">
                    {components.map((item, index) => (
                        <Flex key={element.id + item.compName}
                        justifyContent="space-between" alignItems="center"
                        pt={1} pb={1} pl={2} pr={2}>
                        <Input textAlign="center" readOnly id={element.id+item.compName+"Name_Input"} size="sm" value={item.compName}></Input>
                        <Input textAlign="center" readOnly id={element.id+item.compName+"M_Input"} size="sm" value={item.mW + "(g/Mol)"}></Input>
                        <Button color="red" bgColor={"white"} _hover={{bgColor: "lightgrey"}} size="sm" id={element.id+'Remove_Button' + item.compName} onClick={() => removeComponent(item.compName)}> X </Button>
                        </Flex>
                    ))}
                    </Box>
                    <div className="component-addition-table" style={{overflow: "visible"}}>
                        <AsyncSelect isClearable defaultOptions loadOptions={promiseOptions} onChange={handleChange} value={compoundInput}/>
                        <Input className="stream-property-input" size="md" fontSize={"small"} id={element.id+"New_M_Input"} value={compoundMWeight} placeholder="M (g/mol)" readOnly></Input>
                        <Button className="stream-property-button" colorScheme="blue" variant="outline" id={element.id + 'Component_Add_Button'} onClick={handleClickAddComponent}>Add</Button>
                    </div>
                    
                    <Button className="stream-property-button" size="sm" width="50%" mt="1" mb="1" colorScheme='blue' ml="3" variant="link" alignSelf={"left"} id={element.id + 'Component_Add_Button'} onClick={handleAddCustomCompoundClick} style={{float:"left"}}>Add Custom Compound</Button>
                    <Flex alignContent={"center"} mt="1" ml="5" mb="2">
                    <Button className="stream-property-button" size="sm" mr="1" width="20%" colorScheme='red' alignSelf={"center"} id={element.id + 'Component_Add_Button'} onClick={handleClose} style={{float:"right"}}>Cancel</Button>
                    {(components.length > 0 || componentsToRemove.length > 0) && (<Button className="stream-property-button" width="20%" colorScheme='blue' size="sm" alignSelf={"center"} id="FinishAddingComponentsButton" onClick={handleFinish}>Done</Button>)}
                    </Flex>
                </>
            )}
            {addingCompound && (
                <>
                    <Flex
                        borderBottom='1px solid #eee'
                        justifyContent="space-between" alignItems="center"
                        pt={0} pb={2} pl={2} pr={2}
                        >
                        
                        <Heading size="sm" color="black">Add Compound</Heading>
                    </Flex>
                    <Flex className="component-addition-table" style={{overflow: "visible"}}>
                        <Input fontSize={"smaller"} className="stream-property-input" id={element.id+"New_Compound_Input"} placeholder="Compound Name" value={compoundName} onChange={(e) => setCompoundName(e.target.value)}></Input>
                        <Input
                            fontSize={"smaller"}
                            className="stream-property-input"
                            id={element.id + "New_M_Input"}
                            placeholder="M (g/mol)"
                            value={compoundMWeight}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Check if the input is empty or numeric and non-negative
                                if (value === "" || (/^[+]?\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                                    setCompoundMWeight(value);
                                }
                            }}
                        />
                        <Input fontSize={"smaller"} className="stream-property-input" id={element.id+"New_MF_Input"} placeholder="Molecular Formula" value={compoundMFormula} onChange={(e) => setCompoundMFormula(e.target.value)}></Input>
                    </Flex>
                    <Box ml="20px" mr="20px">
                    <AsyncSelect isDisabled={isDeleteCompoundListDisabled} isClearable defaultOptions loadOptions={getCompoundsList} onChange={e => {setCompoundToDelete(e); setIsDeleteCompoundDisabled(e === null)}} value={compoundToDelete}/>
                    </Box>
                    <Flex className="component-addition-table" style={{overflow: "visible"}} alignContent={"center"}>
                        <Button colorScheme="blue" id={element.id + 'Component_Cancel_Button'} onClick={handleCancel}>Cancel</Button>
                        <Button colorScheme="blue" id={element.id + 'Component_Add_Button'} isDisabled={isAddCompoundDisabled} onClick={handleAddCompound}>Add</Button>
                        <Button colorScheme="red" id={element.id + 'Component_Delete_Button'} isDisabled={isDeleteCompoundDisabled} onClick={handleDeleteCompound}>Delete</Button>
                    </Flex>
                </>
            )}
            </>
        </Card>
    )
}

export default FeedTagWizard;